/**
 * Validación de parámetros de búsqueda avanzada (lógica pura, sin React)
 */

import { z } from "zod";
import type { BusquedaParams } from "@/hooks/use-busqueda-avanzada";

const MIN_LENGTH = 2;

const TEXT_FIELDS = ["identidad", "nombres", "apellidos", "numero_orden", "examen"] as const;

export const busquedaSchema = z
  .object({
    identidad: z.string().optional(),
    nombres: z.string().optional(),
    apellidos: z.string().optional(),
    numero_orden: z.string().optional(),
    examen: z.string().optional(),
    sucursal: z.number().optional(),
    fecha_desde: z.string().optional(),
    fecha_hasta: z.string().optional(),
  })
  .refine(
    (data) => {
      return TEXT_FIELDS.some((f) => {
        const v = data[f];
        return v && v.trim().length >= MIN_LENGTH;
      });
    },
    { message: "at_least_one_field_required", path: ["_form"] }
  )
  .superRefine((data, ctx) => {
    for (const field of TEXT_FIELDS) {
      const value = data[field];
      if (value && value.trim().length > 0 && value.trim().length < MIN_LENGTH) {
        ctx.addIssue({
          code: z.ZodIssueCode.too_small,
          minimum: MIN_LENGTH,
          origin: "string",
          inclusive: true,
          path: [field],
          message: "min_length",
        });
      }
    }
  });

export type BusquedaFormData = z.input<typeof busquedaSchema>;

/** Verifica si al menos un campo de texto tiene valor válido. */
export function hasValidSearchParams(data: BusquedaFormData): boolean {
  return TEXT_FIELDS.some((f) => {
    const v = data[f];
    return v && v.trim().length >= MIN_LENGTH;
  });
}

/** Limpia los parámetros: elimina campos vacíos y hace trim. */
export function cleanSearchParams(data: BusquedaFormData): BusquedaParams {
  const cleaned: Record<string, string | number> = {};
  for (const field of TEXT_FIELDS) {
    const value = data[field]?.trim();
    if (value && value.length >= MIN_LENGTH) {
      cleaned[field] = value;
    }
  }
  if (data.sucursal) cleaned.sucursal = data.sucursal;
  if (data.fecha_desde) cleaned.fecha_desde = data.fecha_desde;
  if (data.fecha_hasta) cleaned.fecha_hasta = data.fecha_hasta;
  return cleaned as BusquedaParams;
}
